import { z } from "zod";

import { CLINIC_FAQ_SCOPE, PATIENT_RECORDS_SCOPE } from "@/shared/agent-prompts/prompts";
import { VoiceIntent, VoiceIntentDisposition } from "@/shared/voice-intent-ledger";

export { VoiceIntent, VoiceIntent as QaIntent } from "@/shared/voice-intent-ledger";

export enum QaIntentDisposition {
  COMPLETED = "completed",
  NOT_COMPLETED = "not_completed",
}

export const voiceIntentSchema = z.enum(VoiceIntent).describe(`
A distinct caller need expressed anywhere in the call, including an agreed alternative that was actually requested.
- schedule: book a new appointment, for either a new or established patient.
- reschedule: move an existing appointment; cancel: cancel an appointment; confirm: confirm an upcoming appointment.
- authorization_or_referral_question: referral or prior authorization status.
- billing_or_pricing: billing, charges, payment, or pricing; insurance_question: insurance coverage or network questions.
- clinical_question: symptoms or clinical advice; prescription_refill: refill or renew a prescription.
- general_clinic_question: ${CLINIC_FAQ_SCOPE}.
- forms_or_medical_records: ${PATIENT_RECORDS_SCOPE}.
- message_for_staff: leave a message for a person or department, including an agreed staff-message alternative.
- telehealth_or_connection_help: help joining a telehealth visit or technical support.
- test_results: request test or imaging results.
- urgent_or_emergency: urgent or emergency routing where connection to the dedicated route is the requested service.
- language_support: interpreter or other-language assistance.
- non_recorded_line: request the non-recorded line; extension_dial: request a known extension. Recorded keypad selections count.
- provider_transfer: a clinician calling to reach staff; requested_human: an explicit request to speak to a human.
- spam: unsolicited marketing, scams, or robocalls unrelated to patient care.
- other: a clearly stated need outside these categories; unconfirmed: no caller need can be determined.
Do not invent an additional intent for routine workflow steps or an offered alternative the caller did not accept.
`);

export const intentDispositionSchema = z.enum(QaIntentDisposition).describe(`
Whether this individual caller need was fulfilled during the call, independently of overall callOutcome.
completed requires evidence in the conversation or successful tool results, not a promise, offer, or failed attempt.
For confirm, reading the appointment details to the caller completes the request even if the caller disconnects before acknowledging them or hearing the closing; a lookup alone or an interrupted readout does not.
An unresolved scheduling, billing, or clinical request remains not_completed after a handoff or staff message.
A successful connection completes an explicit requested_human, provider_transfer, extension_dial, or non_recorded_line request.
A successfully submitted staff message completes message_for_staff; it does not complete the original unresolved request.
When several asks share an intent category, mark completed only if all those asks were fulfilled; otherwise not_completed.
Use not_completed for unconfirmed. For spam, completed means the solicitation was appropriately declined or ended.
`);

export const voiceIntentResultSchema = z.object({
  intent: voiceIntentSchema,
  disposition: intentDispositionSchema,
});

export const voiceIntentResultsSchema = z
  .array(voiceIntentResultSchema)
  .min(1)
  .refine((results) => new Set(results.map(({ intent }) => intent)).size === results.length, {
    message: "Each intent category must appear exactly once",
  })
  .refine((results) => results.length === 1 || results.every(({ intent }) => intent !== VoiceIntent.UNCONFIRMED), {
    message: "Unconfirmed cannot be combined with identified intents",
  })
  .refine(
    (results) =>
      results.every(
        ({ intent, disposition }) =>
          intent !== VoiceIntent.UNCONFIRMED || disposition === QaIntentDisposition.NOT_COMPLETED
      ),
    { message: "An unconfirmed intent cannot be completed" }
  )
  .describe(
    "All distinct caller needs across the full call, one entry per intent category, each assessed independently. Include completed and unresolved needs. Return only unconfirmed/not_completed when no need can be determined."
  );

export type VoiceIntentResult = z.infer<typeof voiceIntentResultSchema>;

export const postCallIntentOccurrenceSchema = z.object({
  intent: voiceIntentSchema,
  disposition: z.enum(VoiceIntentDisposition).describe(`
The operational result for this specific occurrence.
- completed: a successful action directly fulfilled the request.
- escalated: a successful staff message or connected human transfer handed off the unresolved request.
- abandoned: neither fulfillment nor handoff succeeded, including a disconnect, decline, or failed action.
- withdrawn: the caller explicitly replaced this request with a later occurrence. Do not use this for a disconnect, failed action, or an alternative that was only mentioned or offered.
A submitted fallback message is a separate completed message_for_staff occurrence. Its parent request is escalated, not abandoned, even though intentResults marks that request not_completed.
`),
  parentOccurrenceIndex: z
    .number()
    .int()
    .nonnegative()
    .nullable()
    .describe(
      "For an accepted message_for_staff fallback, the zero-based index of the original unresolved occurrence in this array; otherwise null."
    ),
});

/** The model-facing shape; contract rules Gemini cannot express are repaired after parsing. */
export const postCallIntentOccurrencesResponseSchema = z
  .array(postCallIntentOccurrenceSchema)
  .describe(
    "Every caller-need occurrence in chronological order. Keep repeated requests as separate entries. Include only accepted alternatives, never merely offered ones."
  );

export type PostCallIntentOccurrence = z.infer<typeof postCallIntentOccurrenceSchema>;

/**
 * Repairs contract slips the model-facing schema cannot express, so one bad link does not discard the list:
 * unconfirmed yields to identified needs, invalid parent links are dropped, and a submitted fallback
 * message escalates its unresolved parent.
 */
export function repairPostCallIntentOccurrences(
  occurrences: readonly PostCallIntentOccurrence[]
): PostCallIntentOccurrence[] {
  const hasIdentified = occurrences.some(({ intent }) => intent !== VoiceIntent.UNCONFIRMED);
  const keptSourceIndexes = occurrences.flatMap(({ intent }, index) =>
    hasIdentified && intent === VoiceIntent.UNCONFIRMED ? [] : [index]
  );
  const indexBySourceIndex = new Map(keptSourceIndexes.map((sourceIndex, index) => [sourceIndex, index]));
  const repaired = keptSourceIndexes.map((sourceIndex, index): PostCallIntentOccurrence => {
    const occurrence = occurrences[sourceIndex]!;
    const parentIndex =
      occurrence.parentOccurrenceIndex === null ? undefined : indexBySourceIndex.get(occurrence.parentOccurrenceIndex);

    return {
      ...occurrence,
      disposition:
        occurrence.intent === VoiceIntent.UNCONFIRMED ? VoiceIntentDisposition.ABANDONED : occurrence.disposition,
      parentOccurrenceIndex:
        occurrence.intent === VoiceIntent.MESSAGE_FOR_STAFF && parentIndex !== undefined && parentIndex < index
          ? parentIndex
          : null,
    };
  });

  for (const { disposition, parentOccurrenceIndex } of repaired) {
    const parent = parentOccurrenceIndex === null ? undefined : repaired[parentOccurrenceIndex];

    if (
      parent &&
      disposition === VoiceIntentDisposition.COMPLETED &&
      parent.disposition !== VoiceIntentDisposition.COMPLETED
    ) {
      parent.disposition = VoiceIntentDisposition.ESCALATED;
    }
  }

  return repaired;
}

export const postCallIntentOccurrencesSchema = postCallIntentOccurrencesResponseSchema.min(1);
